import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { persistReducer, createMigrate } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';
import {
  PERSIST_SLICE_KEYS,
  mergePersistedTabState,
  restorePersistedTabState,
} from './persistSlices';
import checkoutReducer from './checkout';
import errorReducer from './error';
import eventsReducer from './events';
import loaderReducer from './loader';
import plansReducer from './plans';
import signupReducer from './signup';
import appReducer from './app';
import googleAdsReducer from './googleAds';
import mapReducer from './map';
import { IS_PROD } from '../constants';

const migrations = {
  0: (state: any) => ({
    ...state,
  }),
};

const reducers = combineReducers({
  checkout: checkoutReducer,
  error: errorReducer,
  events: eventsReducer,
  loader: loaderReducer,
  plans: plansReducer,
  signup: signupReducer,
  app: appReducer,
  map: mapReducer,
  googleAds: googleAdsReducer,
});

const rootReducer = (state: ReturnType<typeof reducers> | undefined, action: any) => {
  if (restorePersistedTabState.match(action)) {
    return mergePersistedTabState(
      state ?? ({} as ReturnType<typeof reducers>),
      action.payload,
    ) as ReturnType<typeof reducers>;
  }

  return reducers(state, action);
};

const persistConfig = {
  key: 'root',
  storage: sessionStorage,
  whitelist: [...PERSIST_SLICE_KEYS],
  version: 0,
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: !IS_PROD,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export default store;
